import { z } from "zod";

/**
 * The closed audit vocabulary.
 *
 * Twelve opportunity types from the book, plus the administrative (user-keyed) surface.
 * Adding an operation means adding its type here first — the pipeline requires one, so
 * an operation shipped without an event type fails typecheck rather than review.
 */
export const AUDIT_EVENT_TYPES = {
  // Opportunity — the closed twelve.
  created: "created",
  clientChanged: "client_changed",
  nameChanged: "name_changed",
  nipChanged: "nip_changed",
  expectedCloseDateChanged: "expected_close_date_changed",
  stageChanged: "stage_changed",
  closedWon: "closed_won",
  closedLost: "closed_lost",
  reopened: "reopened",
  ownerReassigned: "owner_reassigned",
  /**
   * The free-text note changed — added, rewritten or removed.
   *
   * A thirteenth opportunity type, added 2026-08-20 with the notes field. The vocabulary is
   * closed, not frozen: the rule is that an operation cannot ship without its event type,
   * and a note that changed with no record of it would be the one editable value on the
   * opportunity that the history did not know about.
   */
  notesChanged: "notes_changed",
  /** The mandatory reason a deal was lost. Its own event: a required field, not commentary. */
  lostReasonChanged: "lost_reason_changed",
  deleted: "deleted",
  restored: "restored",

  // Administrative surface — keyed to a user rather than an opportunity.
  userInvited: "user_invited",
  invitationResent: "invitation_resent",
  invitationRevoked: "invitation_revoked",
  credentialCreated: "credential_created",
  passwordReset: "password_reset",
  roleChanged: "role_changed",
  userDeactivated: "user_deactivated",
  userReactivated: "user_reactivated",
  colorChanged: "color_changed",

  // Configuration — keyed to a stage or a setting rather than to a record.
  //
  // These exist because a stage rename or a threshold change reshapes the board for
  // everyone at once: "why did half the pipeline become stalled last Tuesday" had no
  // answer while configuration was the one mutable thing nothing recorded.
  stageCreated: "stage_created",
  stageRenamed: "stage_renamed",
  stageTypeChanged: "stage_type_changed",
  /** A stage's palette colour. Configuration, so it reshapes the board for everyone. */
  stageColorChanged: "stage_color_changed",
  stageReordered: "stage_reordered",
  stageDeactivated: "stage_deactivated",
  stageReactivated: "stage_reactivated",
  settingChanged: "setting_changed",
} as const;

export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[keyof typeof AUDIT_EVENT_TYPES];

/**
 * The four things an audit event can be *about*, and therefore the four scope columns.
 *
 * Declared here rather than in the API because it is now shared vocabulary: the activity
 * feed's categories are groups of these, and the role that may read a scope is a contract
 * both sides state the same way. `event-defs.ts` imports it, so the append validator and
 * the feed's `where` clause cannot disagree about what a scope is called.
 */
export const AUDIT_SCOPES = ["opportunity", "user", "stage", "setting"] as const;

export type AuditScope = (typeof AUDIT_SCOPES)[number];

export const auditScopeSchema = z.enum(AUDIT_SCOPES);

export const AUDIT_EVENT_TYPE_VALUES = Object.values(AUDIT_EVENT_TYPES) as [
  AuditEventType,
  ...AuditEventType[],
];

export const auditEventTypeSchema = z.enum(AUDIT_EVENT_TYPE_VALUES);

/**
 * One audit row as the client reads it.
 *
 * `oldValue`/`newValue` hold **display** values, stamped at write time — so a client
 * renamed next year does not rewrite what the history says happened. Machine ids live in
 * `metadata` for linking.
 */
export const auditEventSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  eventType: auditEventTypeSchema,
  actorId: z.string().uuid(),
  actorName: z.string(),
  /**
   * Scope: exactly one of these is set, enforced by a database CHECK as well as by the
   * append validator. An event belonging to nothing is unreachable from any timeline; one
   * belonging to two things would appear twice.
   */
  opportunityId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  stageId: z.string().uuid().nullable(),
  settingKey: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

/** Cursor pagination over `(occurredAt desc, id desc)`. */
export const auditTimelineResponseSchema = z.object({
  items: z.array(auditEventSchema),
  nextCursor: z.string().nullable(),
});

export type AuditTimelineResponse = z.infer<typeof auditTimelineResponseSchema>;

/** The book's recommendation: ten most recent, then "show all history". */
export const AUDIT_TIMELINE_PAGE_SIZE = 10;

/**
 * How much of a free-text value an audit row carries.
 *
 * Notes are unbounded prose, and `old_value`/`new_value` are **display** values stamped at
 * write time — a timeline row is one line, not a document. Truncating at write time rather
 * than at render time keeps the store from growing a copy of every draft of every note, and
 * means every reader of the timeline sees the same string.
 */
export const AUDIT_VALUE_DISPLAY_MAX = 120;

/** Truncate a display value for the audit store. `null` stays `null` — "there was none". */
export function auditDisplayValue(value: string | null): string | null {
  if (value === null) return null;
  return value.length <= AUDIT_VALUE_DISPLAY_MAX ? value : `${value.slice(0, AUDIT_VALUE_DISPLAY_MAX - 1)}…`;
}
